import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./search.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import { Variant as ButtonVariant } from 'wds/button';
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import equalityFn from 'zustand/shallow';
import { totalFilterSearchResults } from '~/modules/helpers/filterHelper';
import { asyncGetFocusTrap } from '~/modules/helpers/focusTrap';
import { sortOptionsForType } from '~/modules/helpers/searchSortOptionsHelper';
import { buildNavigationUrl } from '~/modules/helpers/searchUrlHelper';
import { featureFlagsStore, selectFeatureFlagsLoaded } from '~/modules/stores/feature_flags_store';
import { WireI18n, i18nStore, selectSearchLabels } from '~/modules/stores/i18nStore';
import { navigate } from '~/modules/stores/routerStore';
import { filtersStore, selectFilterChipsByType, selectFiltersByType } from '~/modules/stores/searchFiltersStore';
import { FilterParams, searchPageSelector, searchStore } from '~/modules/stores/searchStore';
import { DeviceType, selectDeviceType, viewportStore } from '~/modules/stores/viewportStore';
import { images } from '~/modules/utilities/assetUtilities';
class SearchPage extends LightningElement {
  constructor(...args) {
    super(...args);
    this.currentKeyTotalResults = undefined;
    this.totalCount = {
      apps: -1,
      consultants: -1
    };
    this.totalCountForCurrentType = undefined;
    this.isLoading = false;
    this.searchError = false;
    this.hideSortButton = false;
    this.searchParams = {
      keyword: '',
      sort: undefined,
      type: undefined,
      rankingInformation: undefined,
      filterParams: new FilterParams()
    };
    this.displayMobileFilterPanel = void 0;
    this.firstLoad = true;
    this.noFiltersAppliedImageUrl = images.noFiltersApplied;
    this.deviceType = void 0;
    this.flagsLoaded = void 0;
    this.uniqueFiltersSelected = 0;
    this.filterSections = [];
    this.filterKeyword = undefined;
    this.filterChips = [];
    this.labels = void 0;
  }
  setSearchStoreInfo({
    searchParams,
    noFiltersNoKeyword,
    results,
    totals
  }) {
    this.searchParams = searchParams;
    this.noFiltersNoKeyword = noFiltersNoKeyword;
    this.isLoading = results?.isLoading;
    this.currentKeyTotalResults = results?.totalSearchResults ?? 0;
    this.searchError = results?.isError;
    this.hideSortButton = this.searchError || !this.isLoading && this.currentKeyTotalResults <= 0;
    if (totals) {
      this.totalCount = totals;
      this.totalCountForCurrentType = totals[this.searchParams.type];
    }
  }
  async switchFocus(isPanelOpen) {
    if (isPanelOpen) {
      const filters = await asyncGetFocusTrap(this.template, '.filters');
      filters?.focusFirstElement();
    } else {
      this.template.querySelector('.add-filter-button').focus();
    }
  }
  switchMobileFilterPanel() {
    this.switchFocus(!this.displayMobileFilterPanel);
    this.displayMobileFilterPanel = !this.displayMobileFilterPanel;
  }
  get mobileFilterPanel() {
    return this.deviceType !== DeviceType.DESKTOP;
  }
  get isMobilePhone() {
    return this.deviceType === DeviceType.PHONE;
  }
  get shouldDisplayFilters() {
    return !this.mobileFilterPanel || this.displayMobileFilterPanel;
  }
  get popoverOrientation() {
    const popoverOrientation = {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.CENTER
    };
    return popoverOrientation;
  }
  get contentGap() {
    return !this.mobileFilterPanel ? 'x-large' : 'none';
  }
  get addFilterButtonText() {
    const {
      i18n
    } = i18nStore.getState();
    if (this.uniqueFiltersSelected > 0) {
      return i18n.search.filtersButtonWithAmount({
        amount: this.uniqueFiltersSelected
      });
    }
    return this.labels.addFiltersButton;
  }
  get closeButtonText() {
    if (this.isLoading) {
      return this.labels.loading;
    }
    const {
      i18n
    } = i18nStore.getState();
    return this.filterSections?.some(filter => filter.selectedValue) ? i18n.search.showFilterResults({
      amount: this.currentKeyTotalResults
    }) : this.labels.closeFilterPanel;
  }
  get buttonVariant() {
    return this.isMobilePhone ? ButtonVariant.Secondary : ButtonVariant.Tertiary;
  }
  filterUpdate([section, filterKeyword, chips, openFilterPanel]) {
    this.filterSections = section;
    this.filterKeyword = filterKeyword;
    this.filterChips = chips;
    this.uniqueFiltersSelected = chips.length;
    if (this.firstLoad) {
      this.firstLoad = false;
      this.displayMobileFilterPanel = openFilterPanel;
    }
  }
  handleSortSelectionChange(event) {
    const {
      value: [selectedOption]
    } = event.detail;
    const url = buildNavigationUrl(this.searchParams, {
      sort: selectedOption,
      type: this.searchParams.type
    });
    navigate(url);
  }
  handleSwitch(event) {
    this.uniqueFiltersSelected = 0;
    const type = event.detail.type;
    navigate(buildNavigationUrl(this.searchParams, {
      type,
      resetState: true
    }));
  }
  get searchTitle() {
    if (this.searchParams.keyword) {
      return i18nStore.getState().i18n.search.keywordResults({
        KEYWORD: this.searchParams.keyword || ''
      });
    }
    return this.labels.exploreAppExchange;
  }
  get sortOptions() {
    return sortOptionsForType(this.searchParams.type || 'apps', this.labels.sort);
  }
  get selectedSortOption() {
    return this.searchParams.sort || 'relevant';
  }
  buildFilterUrl(filterKey, filterValue) {
    return buildNavigationUrl(this.searchParams, {
      filter: {
        key: filterKey,
        value: filterValue
      },
      type: this.searchParams.type
    });
  }
  handleFilterSelectionChange(event) {
    const selectedOption = event.detail;
    this.changeFilter(selectedOption.section, selectedOption.value);
  }
  changeFilter(section, value) {
    const url = this.buildFilterUrl(section, value);
    navigate(url);
  }
  handleFilterAnalytics(event) {
    const url = this.buildFilterUrl(event.detail.section, [event.detail.value]);
    const filterInputResultCount = totalFilterSearchResults(this.filterSections);
    event.detail.searchContext = {
      url,
      keyword: this.searchParams.keyword,
      sectionLabel: event.detail.sectionLabel,
      optionLabel: event.detail.value,
      filterKeyword: this.filterKeyword,
      searchTab: this.searchParams.type,
      searchResults: this.totalCountForCurrentType,
      searchResultsCountPerType: this.totalCount,
      searchResultCount: filterInputResultCount
    };
  }
  handleSortAnalytics(event) {
    const {
      elementType
    } = event.detail;
    if (elementType === 'filter') {
      event.detail.keyword = this.searchParams.keyword;
      event.detail.sortLabel = this.labels.sort.tooltip;
      event.detail.navigateTo = buildNavigationUrl(this.searchParams, {
        type: this.searchParams.type,
        resetState: true
      });
    }
  }
  get showFilterChips() {
    return this.filterChips?.length > 0 && !this.mobileFilterPanel;
  }
  get mobileFilterChips() {
    return this.filterChips?.length > 0 && this.mobileFilterPanel;
  }
  get filterByLabel() {
    const {
      i18n
    } = i18nStore.getState();
    if (this.searchParams.keyword) {
      return i18n.search.filteredBy({
        showing: this.currentKeyTotalResults || 0,
        total: this.totalCountForCurrentType || 0
      });
    }
    return i18n.search.keywordlessFilteredBy({
      showing: this.currentKeyTotalResults || 0
    });
  }
  get filterByAria() {
    return this.filterChips.map(chip => chip.label).join(', ');
  }
  get showingResultsLabel() {
    const {
      i18n
    } = i18nStore.getState();
    // @ts-expect-error * Automated Type Error Fix
    const totalCount = this.totalCount[this.searchParams?.type];
    return i18n.search.showingResults({
      total: totalCount
    });
  }
  handleChipClosed(event) {
    const filterToRemove = event.detail;
    if (filterToRemove.section === 'location') {
      this.closeLocationChip(filterToRemove);
    } else {
      // @ts-expect-error * Automated Type Error Fix
      const oldValues = [this.searchParams.filterParams[filterToRemove.section]].flat();
      const newValues = oldValues.filter(value => value !== filterToRemove.value);
      this.changeFilter(filterToRemove.section, newValues);
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  closeLocationChip(filterToRemove) {
    // @ts-expect-error * Automated Type Error Fix
    const oldValue = this.searchParams.filterParams[filterToRemove.section];
    const [country, state] = oldValue.split('-');
    if (country === filterToRemove.value) {
      // @ts-expect-error * Automated Type Error Fix
      this.changeFilter(filterToRemove.section, undefined);
    }
    if (state === filterToRemove.value) {
      this.changeFilter(filterToRemove.section, [`${country}-*`]);
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SearchPage, {
  track: {
    displayMobileFilterPanel: 1
  },
  wire: {
    setSearchStoreInfo: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: searchStore,
          selector: searchPageSelector,
          equalityFn
        };
      }
    },
    deviceType: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: viewportStore,
          selector: selectDeviceType
        };
      }
    },
    flagsLoaded: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: featureFlagsStore,
          selector: selectFeatureFlagsLoaded
        };
      }
    },
    filterUpdate: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: filtersStore,
          selector: state => [selectFiltersByType(state), state.filterKeyword, selectFilterChipsByType(), state.openFilterPanel],
          equalityFn
        };
      }
    },
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSearchLabels
        };
      }
    }
  },
  fields: ["currentKeyTotalResults", "totalCount", "totalCountForCurrentType", "isLoading", "searchError", "hideSortButton", "searchParams", "firstLoad", "noFiltersAppliedImageUrl", "uniqueFiltersSelected", "filterSections", "filterKeyword", "filterChips"]
});
const __lwc_component_class_internal = _registerComponent(SearchPage, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;